import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import io from 'socket.io-client';
import '../styles/chat.css';
import { API_URL, SOCKET_URL } from '../utils/api';
import { useNotification } from '../context/NotificationContext';

// Remove the hardcoded URL
// const SOCKET_URL = 'http://localhost:5000';

const Chat = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const socketRef = useRef();
  const userId = user?._id;
  const { notify } = useNotification();

  // Force message re-processing on initial load
  const [messagesProcessed, setMessagesProcessed] = useState(false);

  useEffect(() => {
    // Connect to socket server using the utility-provided URL
    socketRef.current = io(SOCKET_URL);
    
    // Listen for incoming messages
    socketRef.current.on('receive-message', (data) => {
      // Only add the message if it's not from the current user
      // (to avoid duplicates since we add our own messages immediately)
      if (data.sender !== userId) {
        setMessages(prev => [...prev, data]);
        // Only notify if the user is not on the chat page (tab not visible)
        if (document.visibilityState !== 'visible') {
          notify({
            title: 'New Chat Message',
            message: data.text,
            type: 'chat',
            id: `chat-${data._id}`,
            date: data.timestamp
          });
        }
      }
    });
    
    // Fetch initial messages
    fetchMessages();
    
    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [userId, notify]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add this effect to normalize message format after loading
  useEffect(() => {
    if (messages.length > 0 && !messagesProcessed) {
      console.log('Processing messages for alignment...');
      
      // Normalize messages to ensure consistent format
      const normalizedMessages = messages.map(message => {
        console.log('Message format:', {
          id: message._id,
          sender: message.sender,
          senderType: typeof message.sender,
          currentUser: userId
        });
        
        // Create a clean copy with consistent sender format
        return {
          ...message,
          // Ensure sender is always just the ID string for consistent comparison
          _senderId: typeof message.sender === 'object' ? message.sender._id : message.sender
        };
      });
      
      setMessages(normalizedMessages);
      setMessagesProcessed(true);
    }
  }, [messages, messagesProcessed, userId]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Process the messages to ensure consistent format
      const processedMessages = response.data.map(msg => ({
        ...msg,
        // Add a consistent _senderId field for comparison
        _senderId: typeof msg.sender === 'object' ? msg.sender._id : msg.sender
      }));
      
      setMessages(processedMessages);
      setMessagesProcessed(true);
      setLoading(false);
      setError('');
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages. Please try again.');
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const messageText = newMessage.trim();
    if (!messageText) return;

    // Clear input immediately
    setNewMessage('');
    
    // Create a temporary message object with a temporary ID
    const tempMessage = {
      _id: 'temp-' + Date.now(),
      text: messageText,
      sender: userId,
      _senderId: userId, // Add this for consistent comparison
      timestamp: new Date().toISOString()
    };
    
    // Add message to the UI immediately
    setMessages(prev => [...prev, tempMessage]);
    
    // Scroll to bottom
    scrollToBottom();

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/chat`, {
        message: messageText
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Add the _senderId field for consistency
      const processedMessage = {
        ...response.data,
        _senderId: typeof response.data.sender === 'object' 
          ? response.data.sender._id 
          : response.data.sender
      };
      
      // Replace the temporary message with the actual one from the server
      setMessages(prev => prev.map(msg => 
        msg._id === tempMessage._id ? processedMessage : msg
      ));
      
      // Emit the new message via socket for other users
      if (socketRef.current) {
        socketRef.current.emit('send-message', {
          roomId: 'general', // Using a general room for now
          ...processedMessage
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove the temporary message if there was an error
      setMessages(prev => prev.filter(msg => msg._id !== tempMessage._id));
      
      setError('Failed to send message. Please try again.');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/chat/${messageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Update the messages list
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message. Please try again.');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const clearError = () => {
    setError('');
  };

  if (!user?.partner || user?.deletionRequestedAt) {
    return <div style={{textAlign: 'center', marginTop: 40, color: '#888'}}>You must be in a couple to use chat.</div>;
  }

  if (loading) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <h2>Our Private Chat</h2>
        </div>
        <div className="messages-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner"></div>
            <p>Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h2>Our Private Chat</h2>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
          <button 
            onClick={clearError} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '10px' }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      )}
      
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-state">
            <ChatIcon style={{ fontSize: 48 }} />
            <p>No messages yet</p>
            <p>Start the conversation by sending a message below</p>
          </div>
        ) : (
          messages.map((message) => {
            // Use the normalized _senderId field for more consistent comparison
            const isSender = message._senderId === userId;
            
            return (
              <div
                key={message._id}
                className={`message ${isSender ? 'sent' : 'received'}`}
              >
                {isSender && (
                  <div className="message-actions">
                    <button 
                      className="delete-message-btn"
                      onClick={() => handleDeleteMessage(message._id)}
                      title="Delete message"
                    >
                      <DeleteIcon style={{ fontSize: 14 }} />
                    </button>
                  </div>
                )}
                
                <div className="message-content">
                  {message.text}
                </div>
                <div className="message-time">
                  {formatTime(message.timestamp)}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={handleSendMessage} className="message-form">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="message-input"
        />
        <button type="submit" className="send-button">
          Send
        </button>
      </form>
    </div>
  );
};

export default Chat; 